import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import CinemaFooter from '../components/gallery/cinema/CinemaFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ShowcasePage = () => {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPortfolioGalleries();
  }, []);

  const loadPortfolioGalleries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/showcase/galleries`);
      setGalleries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading portfolio galleries:', error);
      toast.error('Failed to load portfolio galleries');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryClick = (galleryId) => {
    navigate(`/portfolio/${galleryId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Loading showcase…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tiq-nearblack font-tiq-body text-white">
      <Toaster position="top-center" richColors />

      {/* Editorial hero */}
      <header className="relative overflow-hidden" style={{ padding: 'clamp(56px,9vw,120px) clamp(16px,3vw,38px) clamp(36px,5vw,64px)' }}>
        <img
          src="/turbo-mark-white.png"
          alt=""
          className="absolute pointer-events-none"
          style={{ top: 'clamp(-60px,-5vw,-30px)', right: 'clamp(-60px,-5vw,-30px)', width: 'min(36vw,420px)', opacity: 0.08, transform: 'rotate(-8deg)' }}
        />
        <div className="relative" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <img src="/turbo-logo-white.png" alt="Turbo 360" style={{ height: 26, width: 'auto', marginBottom: 'clamp(28px,5vh,56px)' }} />
          <div className="tiq-kicker" style={{ fontSize: 'clamp(11px,1.4vw,13px)', color: '#BF212F' }}>Selected Galleries</div>
          <div style={{ width: 54, height: 4, background: '#BF212F', margin: 'clamp(14px,2vh,20px) 0 clamp(14px,2vh,20px)' }} />
          <h1 className="font-tiq-display uppercase m-0" style={{ fontWeight: 900, fontSize: 'clamp(46px,9vw,108px)', lineHeight: 0.9, letterSpacing: '-0.025em' }}>
            Our Work
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(15px,2vw,18px)', marginTop: 18, maxWidth: '46ch' }}>
            A selection of recent events, shot from every angle and cut for maximum velocity.
          </p>
        </div>
      </header>

      {/* Gallery Grid */}
      <main style={{ padding: '0 clamp(16px,3vw,38px) clamp(20px,4vw,40px)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {galleries.length === 0 ? (
            <div className="text-center" style={{ padding: '80px 20px' }}>
              <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>No portfolio galleries available yet</p>
            </div>
          ) : (
            <div className="tiq-showcase-grid">
              {galleries.map((gallery) => (
                <div
                  key={gallery.id}
                  onClick={() => handleGalleryClick(gallery.id)}
                  className="group cursor-pointer overflow-hidden tiq-fade"
                  style={{ borderRadius: 12, background: '#141416', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {/* Featured Image (3:2) */}
                  <div className="relative overflow-hidden" style={{ aspectRatio: '3 / 2', background: '#1a1a1c' }}>
                    {gallery.featured_photo_url ? (
                      <div
                        className="w-full h-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                        style={{
                          backgroundImage: `url(${gallery.featured_photo_url})`,
                          backgroundPosition: gallery.featured_photo_focal_point
                            ? `${gallery.featured_photo_focal_point.x}% ${gallery.featured_photo_focal_point.y}%`
                            : 'center',
                        }}
                      />
                    ) : (
                      <div className="w-full h-full" style={{ background: 'linear-gradient(135deg,#BF212F,#8B1923)' }} />
                    )}
                    <span
                      className="font-tiq-mono"
                      style={{ position: 'absolute', top: 10, right: 10, fontSize: 10, letterSpacing: '0.08em', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', color: '#fff', padding: '4px 8px', borderRadius: 999 }}
                    >
                      {gallery.photo_count} PHOTOS
                    </span>
                  </div>

                  {/* Gallery Info */}
                  <div style={{ padding: '18px 18px 20px' }}>
                    <h3 className="font-tiq-display uppercase m-0 transition-colors group-hover:text-[#BF212F]" style={{ fontWeight: 800, fontSize: 19, letterSpacing: '-0.015em', lineHeight: 1.05 }}>
                      {gallery.name}
                    </h3>
                    {gallery.event_date && (
                      <p className="font-tiq-mono" style={{ fontSize: 11, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.5)', marginTop: 10, textTransform: 'uppercase' }}>
                        {new Date(gallery.event_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <CinemaFooter />
      </main>
    </div>
  );
};

export default ShowcasePage;
